import { QueryKeys } from "@/constants/Query";
import axiosInstance from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDebounce } from "react-use";
interface MedicalHistoryConditionSearchParams {
  searchTerm?: string;
  patientId?: string;
  enabled?: boolean;
}

export interface MedicalHistoryCondition {
  conditionSystem: string;
  conditionCode: string;
  conditionDisplay: string;
  existingEpisodeOfCareId?: string;
  existingConditionId?: string;
}

/**
 * Search for SNOMED codes by ECL expression and search term
 * @param searchTerm - The search term to search for
 * @param eclExpression - The ECL expression to search for. Examples:
 * - << 404684003 - Children of parent code 404684003
 * - << 248326004 OR 364712009 - Children of parent code 248326004 or 364712009
 * @param enabled - Whether to enable the query
 * @returns The SNOMED codes
 */
export const useMedHistoryConditionSearch = ({
  searchTerm,
  patientId,
  enabled = true,
}: MedicalHistoryConditionSearchParams) => {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  useDebounce(
    () => {
      setDebouncedSearchTerm(searchTerm);
    },
    500,
    [searchTerm],
  );
  return useQuery<MedicalHistoryCondition[]>({
    queryFn: async () => {
      const response = await axiosInstance.get(
        "/charting/medical-history/conditions",
        {
          params: {
            patientId,
            searchTerm: debouncedSearchTerm,
          },
        },
      );

      return response.data?.results || [];
    },
    queryKey: QueryKeys.Patient.MedicalRecords.Conditions.Search.DEFAULT({
      patientId: patientId as string,
      searchTerm: debouncedSearchTerm as string,
    }),
    enabled:
      enabled &&
      Boolean(
        patientId && debouncedSearchTerm && debouncedSearchTerm.length > 2,
      ),
    staleTime: Number.POSITIVE_INFINITY,
  });
};
